import type {ChangeEvent, ComponentType} from 'react';

import NextLink from 'next/link';
import {useRouter} from 'next/router';

import {Box, Flex, Link, Select, Text} from '@tempo/core';
import {DeezerLogo} from '@tempo/core';
import {FacebookIcon, InstagramIcon, XIcon, YoutubeIcon} from '@tempo/icons';
import {tLocal} from '@modules/i18n/localStrings';
import {LEGAL_NOTICE_PATH, TERMS_PATH} from '@modules/legal/routes';

const LEGAL_HREFS = [TERMS_PATH, LEGAL_NOTICE_PATH];

const getLegalLinks = (locale: string) => {
	const isFrench = locale.toLowerCase().startsWith('fr');
	return [
		{label: isFrench ? 'CGU' : 'Terms of Use', href: LEGAL_HREFS[0]},
		{
			label: isFrench ? 'Mentions légales' : 'Legal Notice',
			href: LEGAL_HREFS[1],
		},
	];
};

const NATIVE_LANGUAGE_NAMES: Record<string, string> = {
	ar: 'العربية',
	bg: 'Български',
	cs: 'Čeština',
	da: 'Dansk',
	de: 'Deutsch',
	el: 'Ελληνικά',
	en: 'English',
	es: 'Español',
	fi: 'Suomi',
	fr: 'Français',
	he: 'עברית',
	hr: 'Hrvatski',
	hu: 'Magyar',
	it: 'Italiano',
	ja: '日本語',
	ko: '한국어',
	mk: 'Македонски',
	nl: 'Nederlands',
	no: 'Norsk',
	pl: 'Polski',
	pt: 'Português',
	ro: 'Română',
	ru: 'Русский',
	sk: 'Slovenčina',
	sl: 'Slovenščina',
	sr: 'Српски',
	sv: 'Svenska',
	tr: 'Türkçe',
	uk: 'Українська',
	'zh-Hans': '简体中文',
	'zh-Hant': '繁體中文',
	zh: '中文',
};

const getLanguageLabel = (locale: string): string => {
	if (NATIVE_LANGUAGE_NAMES[locale]) return NATIVE_LANGUAGE_NAMES[locale];
	const base = locale.split('-')[0];
	if (NATIVE_LANGUAGE_NAMES[base]) return NATIVE_LANGUAGE_NAMES[base];
	return locale;
};

type SocialIcon = ComponentType<{
	size?: 'small' | 'medium';
	color?: string;
	'aria-hidden'?: boolean;
}>;

const SOCIALS: Array<{label: string; href: string; Icon: SocialIcon}> = [
	{label: 'Facebook', href: 'https://facebook.com/deezer', Icon: FacebookIcon},
	{
		label: 'Instagram',
		href: 'https://instagram.com/deezer',
		Icon: InstagramIcon,
	},
	{label: 'X', href: 'https://x.com/deezer', Icon: XIcon},
	{label: 'YouTube', href: 'https://youtube.com/deezer', Icon: YoutubeIcon},
];

export const Footer = () => {
	const router = useRouter();
	const locales = router.locales ?? [];
	const currentLocale = router.locale ?? router.defaultLocale ?? 'en';
	const legalLinks = getLegalLinks(currentLocale);

	const handleLocaleChange = (event: ChangeEvent<HTMLSelectElement>) => {
		const nextLocale = event.target.value;
		router.push(router.asPath, router.asPath, {locale: nextLocale});
	};

	return (
		<Flex
			as="footer"
			data-testid="Footer"
			direction="column"
			alignItems={['stretch', 'stretch', 'flex-end']}
			gap="spacing.l"
			px={['16px', '32px', '80px']}
			py={['spacing.xl', 'spacing.xl', '40px']}
			bg="background.neutral.primary.default"
		>
			<Flex
				w="100%"
				direction={['column', 'column', 'row']}
				alignItems="center"
				justifyContent="space-between"
				gap="spacing.l"
			>
				<Flex
					data-testid="FooterSocials"
					gap="23px"
					alignItems="center"
					justifyContent="center"
				>
					{SOCIALS.map(({label, href, Icon}) => (
						<Link
							key={label}
							href={href}
							aria-label={label}
							color="text.neutral.primary.default"
							display="inline-flex"
							w="20px"
							h="20px"
							alignItems="center"
							justifyContent="center"
						>
							<Icon
								size="small"
								color="text.neutral.primary.default"
								aria-hidden
							/>
						</Link>
					))}
				</Flex>

				<Box
					as={NextLink}
					href="/"
					data-testid="FooterBranding"
					aria-label="Deezer"
					display="inline-flex"
				>
					<DeezerLogo compensate={false} h="48px" />
				</Box>

				<Flex data-testid="FooterLocale" alignItems="center" gap="spacing.s">
					<Text
						data-testid="FooterCopyright"
						variant="body.s.default"
						color="text.neutral.secondary.default"
					>
						{tLocal('local_footer_copyright')}
					</Text>
					<Box>
						<Text
							as="label"
							htmlFor="footer-language-selector"
							variant="body.s.default"
							color="text.neutral.primary.default"
							srOnly
						>
							{tLocal('local_footer_language_label')}
						</Text>
						<Select
							id="footer-language-selector"
							data-testid="FooterLanguageSelect"
							variant="filled"
							value={currentLocale}
							onChange={handleLocaleChange}
							size="small"
						>
							{locales.map((loc) => (
								<option key={loc} value={loc}>
									{getLanguageLabel(loc)}
								</option>
							))}
						</Select>
					</Box>
				</Flex>
			</Flex>

			<Flex
				data-testid="FooterLegal"
				gap="spacing.s"
				flexWrap="wrap"
				justifyContent={['center', 'center', 'flex-end']}
			>
				{legalLinks.map((link) => (
					<Link
						key={link.label}
						href={link.href}
						color="text.neutral.secondary.default"
						textDecoration="underline"
					>
						<Text as="span" variant="body.s.default">
							{link.label}
						</Text>
					</Link>
				))}
			</Flex>
		</Flex>
	);
};
